import { adoptAccountLocale, LOCALE, takePendingLocale, t } from "../i18n";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, logoutRequest, onSessionExpired, refreshAccessToken, setAccessToken } from "../api/client";
import type { Me, Membership } from "../api/types";
import { navigate } from "../router/router";

type Status = "loading" | "anonymous" | "authenticated";

interface AuthState {
  status: Status;
  me: Me | null;
  membership: Membership | null;
  selectOrganization: (orgId: string) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);
const ORG_KEY = "eapace.currentOrg"; // apenas o id da organização escolhida, sem dados sensíveis

function readStoredOrg(): string | null {
  try {
    return localStorage.getItem(ORG_KEY);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const [orgId, setOrgId] = useState<string | null>(readStoredOrg);

  const loadMe = useCallback(async () => {
    const data = await api.get<Me>("/auth/me");
    // idioma: escolha feita neste navegador antes do login vira a da conta; senão, vale a da conta
    if (takePendingLocale()) {
      if (data.locale !== LOCALE) await api.patch("/auth/me", { locale: LOCALE }).catch(() => undefined);
    } else if (adoptAccountLocale(data.locale)) {
      window.location.reload();
      return;
    }
    setMe(data);
    setStatus("authenticated");
  }, []);

  useEffect(() => {
    const again = () => void loadMe().catch(() => undefined);
    window.addEventListener("eapace:password-change-required", again);
    return () => window.removeEventListener("eapace:password-change-required", again);
  }, [loadMe]);

  useEffect(() => {
    onSessionExpired(() => {
      setAccessToken(null);
      setMe(null);
      setStatus("anonymous");
    });
    (async () => {
      if (await refreshAccessToken()) {
        try {
          await loadMe();
          return;
        } catch {
          /* cai para anônimo */
        }
      }
      setStatus("anonymous");
    })();
  }, [loadMe]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.post<{ access_token: string }>("/auth/login", { email, password });
      setAccessToken(res.access_token);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      setAccessToken(null);
      setMe(null);
      setStatus("anonymous");
      navigate("/entrar", { replace: true });
    }
  }, []);

  const membership = useMemo(() => {
    if (!me || me.organizations.length === 0) return null;
    return me.organizations.find((o) => o.organization_id === orgId) ?? me.organizations[0];
  }, [me, orgId]);

  const selectOrganization = useCallback((id: string) => {
    setOrgId(id);
    try {
      localStorage.setItem(ORG_KEY, id);
    } catch {
      /* armazenamento indisponível: seleção vale só nesta aba */
    }
  }, []);

  const value: AuthState = {
    status,
    me,
    membership,
    selectOrganization,
    login,
    logout,
    reload: loadMe,
    can: (p) => !!membership?.permissions.includes(p),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error(t("useAuth fora do AuthProvider"));
  return ctx;
}

/** Rota inicial adequada ao perfil. */
export function homePathFor(me: Me, membership: Membership | null): string {
  if (membership) {
    const manages = membership.roles.includes("org_manager") || membership.roles.includes("coach");
    return manages ? `/org/${membership.organization_id}` : `/org/${membership.organization_id}/atleta`;
  }
  if (me.is_platform_admin) return "/admin/assessorias";
  return "/sem-acesso";
}
